import qs from 'qs';
import oikosApi from '../../../services/oikos';
import { validarCNPJ, validarCPF } from '../../../utils/fc';

const PROPOSTA_TP_DOCUMENTO_ID = 37;

export const parseCompraCotacaoInfo = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return raw;

  let current = String(raw).trim();
  for (let depth = 0; depth < 3; depth += 1) {
    try {
      const parsed = JSON.parse(current);
      if (typeof parsed === 'string') {
        current = parsed.trim();
        continue;
      }
      return parsed;
    } catch (error) {
      console.error('Erro ao parsear compraCotacao_info:', error);
      return null;
    }
  }

  return null;
};

const formatFieldLabel = (key) => String(key || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const resolveCadastroTipoFromInfo = (info) => {
  const digits = String(info?.identificacao?.cpf_ou_cnpj || '').replace(/\D/g, '');
  if (validarCPF(digits)) return 'pf';
  if (validarCNPJ(digits)) return 'pj';
  return null;
};

const PJ_DOCUMENTO_KEYS = ['cartao_cnpj', 'contrato_social', 'comprovante_endereco'];

const mapDocumentoEntry = (key, doc = {}) => ({
  key,
  tpDocumento_id: Number(doc?.tpDocumento_id) || null,
  label: doc?.label || formatFieldLabel(key),
  arquivo: doc?.arquivo || '',
  link: doc?.link || '',
  documento_id: Number(doc?.documento_id) || null,
});

export const getCadastroSections = (info) => {
  if (!info) {
    return {
      cadastroTipo: null,
      identificacao: [],
      dadosPessoais: [],
      informacoesEmpresa: [],
      representanteLegal: [],
      gestorContrato: [],
      endereco: [],
      dadosBancarios: [],
      documentosObrigatorios: [],
    };
  }

  const toEntries = (obj = {}) => Object.entries(obj || {}).map(([key, value]) => ({
    key,
    label: key === 'cpf_ou_cnpj' ? 'CPF ou CNPJ' : formatFieldLabel(key),
    value: value == null || value === '' ? '-' : String(value),
  }));

  const mergeUniqueEntries = (primary = [], secondary = []) => {
    const seen = new Set(primary.map((entry) => entry.key));
    return [
      ...primary,
      ...secondary.filter((entry) => !seen.has(entry.key)),
    ];
  };

  const documentosRaw = info?.documentos?.documentos_obrigatorios || {};
  const documentosObrigatoriosFromNested = Object.entries(documentosRaw).map(([key, doc]) => (
    mapDocumentoEntry(key, doc)
  ));

  const cadastroTipo = resolveCadastroTipoFromInfo(info);
  const isPj = cadastroTipo === 'pj';
  const identificacaoEntries = toEntries(info.identificacao);

  let documentosObrigatorios = documentosObrigatoriosFromNested;

  if (isPj) {
    const pjDocumentos = PJ_DOCUMENTO_KEYS
      .filter((key) => info?.documentos?.[key] != null)
      .map((key) => mapDocumentoEntry(key, info.documentos[key]));

    const pjDocumentoKeys = new Set(pjDocumentos.map((doc) => doc.key));
    documentosObrigatorios = [
      ...pjDocumentos,
      ...documentosObrigatoriosFromNested.filter((doc) => !pjDocumentoKeys.has(doc.key)),
    ];
  }

  return {
    cadastroTipo,
    // Mantido vazio na UI: campos de identificação vão para dados pessoais / empresa.
    identificacao: [],
    dadosPessoais: isPj
      ? []
      : mergeUniqueEntries(identificacaoEntries, toEntries(info.dados_pessoais)),
    informacoesEmpresa: isPj
      ? mergeUniqueEntries(identificacaoEntries, toEntries(info.informacoes_empresa))
      : [],
    representanteLegal: isPj ? toEntries(info.representante_legal_empresa) : [],
    gestorContrato: isPj ? toEntries(info.gestor_contrato) : [],
    endereco: toEntries(info.endereco),
    dadosBancarios: toEntries(info.dados_bancarios),
    documentosObrigatorios,
  };
};

const fetchBancoNomeByCod = async (bancoCod) => {
  const cod = String(bancoCod ?? '').trim();
  if (!cod) return null;

  const candidates = [...new Set([
    cod,
    cod.replace(/^0+/, '') || cod,
    cod.padStart(3, '0'),
  ])];

  for (const candidate of candidates) {
    // eslint-disable-next-line no-await-in-loop
    const response = await oikosApi.get(`/?${qs.stringify({
      action: 'get',
      table: 'banco',
      data: { banco_cod: candidate },
    })}`);
    const row = response?.data?.[0];
    if (row?.banco_nome) return String(row.banco_nome).trim();
  }

  return null;
};

const fetchPixTpNomeById = async (pixTpId) => {
  const id = Number(String(pixTpId ?? '').replace(/\D/g, ''));
  if (!id) return null;

  const response = await oikosApi.get(`/?${qs.stringify({
    action: 'get',
    table: 'pixTp',
    data: { pixTp_id: id, pixTp_ativo: 1 },
  })}`);
  const row = response?.data?.[0];
  return row?.pixTp_nome ? String(row.pixTp_nome).trim() : null;
};

/** Substitui códigos numéricos de banco e tipo PIX pelos nomes das tabelas banco/pixTp. */
export const enrichCadastroSectionsWithLookups = async (info) => {
  const sections = getCadastroSections(info);
  if (!info?.dados_bancarios || !sections.dadosBancarios.length) {
    return sections;
  }

  const [bancoNome, pixTpNome] = await Promise.all([
    fetchBancoNomeByCod(info.dados_bancarios.banco),
    fetchPixTpNomeById(info.dados_bancarios.tipo_chave_pix),
  ]);

  return {
    ...sections,
    dadosBancarios: sections.dadosBancarios.map((entry) => {
      if (entry.key === 'banco' && bancoNome) {
        return { ...entry, value: bancoNome };
      }
      if (entry.key === 'tipo_chave_pix' && pixTpNome) {
        return { ...entry, value: pixTpNome };
      }
      return entry;
    }),
  };
};

const mapSourceDocumentToProposta = (doc, source = 'db') => ({
  tpDocumento_id: Number(doc.tpDocumento_id) || null,
  label: doc.tpDocumento_desc || doc.documento_desc || doc.arquivo_desc || doc.arquivo || 'Proposta',
  link: doc.link || (doc.arquivo_caminho ? `https://oikos.ecoss.is/oikos/${String(doc.arquivo_caminho).replace(/^\//, '')}` : null),
  documento_id: Number(doc.documento_id) || null,
  arquivo: doc.arquivo_desc || doc.documento_desc || doc.arquivo || '',
  source,
});

const documentHasPropostaLink = (doc) => Boolean(
  doc?.link
  || String(doc?.arquivo_caminho || '').trim(),
);

/** Última proposta (tpDocumento 37) com arquivo/link — prioriza documento_id mais recente. */
export const getPropostaDocuments = (sourceDocuments = [], info = null) => {
  const docs = sourceDocuments || [];
  const anexarProposta = info?.informacoes_contrato?.anexar_proposta;

  const fromDbTp37 = docs
    .filter((doc) => (
      Number(doc?.tpDocumento_id) === PROPOSTA_TP_DOCUMENTO_ID
      && documentHasPropostaLink(doc)
    ))
    .map((doc) => mapSourceDocumentToProposta(doc, 'db'))
    .sort((a, b) => (Number(b.documento_id) || 0) - (Number(a.documento_id) || 0));

  if (fromDbTp37.length > 0) {
    return [fromDbTp37[0]];
  }

  const propostaDocumentoId = Number(anexarProposta?.documento_id) || 0;
  if (propostaDocumentoId) {
    const fromDbById = docs
      .filter((doc) => (
        Number(doc?.documento_id) === propostaDocumentoId
        && documentHasPropostaLink(doc)
      ))
      .map((doc) => mapSourceDocumentToProposta(doc, 'db'));
    if (fromDbById.length > 0) return fromDbById;
  }

  const propostaTpDocumentoId = Number(anexarProposta?.tpDocumento_id) || 0;
  if (propostaTpDocumentoId) {
    const fromDbByTp = docs
      .filter((doc) => (
        Number(doc?.tpDocumento_id) === propostaTpDocumentoId
        && documentHasPropostaLink(doc)
      ))
      .map((doc) => mapSourceDocumentToProposta(doc, 'db'))
      .sort((a, b) => (Number(b.documento_id) || 0) - (Number(a.documento_id) || 0));
    if (fromDbByTp.length > 0) return [fromDbByTp[0]];
  }

  if (anexarProposta && documentHasPropostaLink(anexarProposta)) {
    return [mapSourceDocumentToProposta(anexarProposta, 'json')];
  }

  return [];
};

const parseCurrencyValue = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return value;
  const normalized = String(value)
    .replace(/[^\d,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getPropostaValorFromInfo = (info) => (
  parseCurrencyValue(info?.informacoes_contrato?.valor_global)
);

const getNomeOuRazaoSocialFromInfo = (info) => {
  const nome = info?.nome_ou_razao_social
    || info?.identificacao?.nome_ou_razao_social
    || info?.dados_pessoais?.nome_ou_razao_social
    || info?.informacoes_empresa?.nome_ou_razao_social;
  return nome ? String(nome).trim() : '';
};

const getContatoFromInfo = (info, { isPf } = {}) => {
  if (isPf) {
    return {
      email: info?.dados_pessoais?.email ? String(info.dados_pessoais.email).trim() : undefined,
      telefone: info?.dados_pessoais?.telefone ? String(info.dados_pessoais.telefone).trim() : undefined,
    };
  }

  const email = info?.informacoes_empresa?.email || info?.dados_pessoais?.email;
  const telefone = info?.informacoes_empresa?.telefone || info?.dados_pessoais?.telefone;
  return {
    email: email ? String(email).trim() : undefined,
    telefone: telefone ? String(telefone).trim() : undefined,
  };
};

const parsePixTpId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const parseEnderecoNumero = (value, isPf) => {
  if (value == null || value === '') return undefined;
  if (isPf) {
    const parsed = Number.parseInt(String(value).replace(/\D/g, ''), 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return String(value).trim();
};

/** Remove pontuação de CPF/CNPJ (só dígitos). */
export const normalizeDocumentoDigits = (value) => String(value || '').replace(/\D/g, '');

const sqlDigitsOnlyColumn = (column) => (
  `REPLACE(REPLACE(REPLACE(REPLACE(IFNULL(${column},''), '.', ''), '-', ''), '/', ''), ' ', '')`
);

const resolveIdentificadorFromInfo = (info) => {
  const digits = normalizeDocumentoDigits(info?.identificacao?.cpf_ou_cnpj);
  if (!digits) return null;

  if (validarCPF(digits)) {
    return { isPf: true, digits };
  }
  if (validarCNPJ(digits)) {
    return { isPf: false, digits };
  }

  return null;
};

export const buildFornecedorPayloadFromInfo = (info, { isPf, userId } = {}) => {
  const identificacao = resolveIdentificadorFromInfo(info);
  if (!identificacao) return null;

  const nomeOuRazaoSocial = getNomeOuRazaoSocialFromInfo(info);
  const { email, telefone } = getContatoFromInfo(info, { isPf: Boolean(isPf) });
  const logradouro = info?.endereco?.logradouro ? String(info.endereco.logradouro).trim() : undefined;
  const numero = parseEnderecoNumero(info?.endereco?.numero, isPf);
  const complemento = info?.endereco?.complemento ? String(info.endereco.complemento).trim() : undefined;
  const banco = info?.dados_bancarios?.banco ? String(info.dados_bancarios.banco).trim() : undefined;
  const conta = info?.dados_bancarios?.conta ? String(info.dados_bancarios.conta).trim() : undefined;
  const agencia = info?.dados_bancarios?.agencia ? String(info.dados_bancarios.agencia).trim() : undefined;
  const pixTpId = parsePixTpId(info?.dados_bancarios?.tipo_chave_pix);
  const chavePix = info?.dados_bancarios?.chave_pix ? String(info.dados_bancarios.chave_pix).trim() : undefined;

  if (isPf) {
    return {
      pf_cpf: identificacao.digits,
      pf_nomeExibicao: nomeOuRazaoSocial || undefined,
      pf_nome: nomeOuRazaoSocial || undefined,
      pf_emailProf: email,
      pf_celProf: telefone,
      pf_endLogr: logradouro,
      pf_endNum: numero,
      pf_endCompl: complemento,
      pf_contaBanco: banco,
      pf_contaNum: conta,
      pf_contaAgencia: agencia,
      pixTp_id: pixTpId,
      pf_chavePix: chavePix,
      pf_fornecedor: 1,
      pf_ativo: 1,
      pf_usuarioMod: userId,
    };
  }

  return {
    pj_cnpj: identificacao.digits,
    pj_razaosocial: nomeOuRazaoSocial || undefined,
    pj_nomefanta: nomeOuRazaoSocial || undefined,
    pj_emailPrin: email,
    pj_telPrin: telefone,
    pj_endLogr: logradouro,
    pj_endNum: numero != null ? String(numero) : undefined,
    pj_endCompl: complemento,
    pj_contaBanco: banco,
    pj_contaNum: conta,
    pj_contaAgencia: agencia,
    pixTp_id: pixTpId,
    pj_chavePix: chavePix,
    pj_fornecedor: 1,
    pj_ativo: 1,
    pj_usuarioMod: userId,
  };
};

export const fetchPfByCpf = async (cpf) => {
  const digits = normalizeDocumentoDigits(cpf);
  if (!digits) return null;

  const response = await oikosApi.get(`/?${qs.stringify({
    action: 'get',
    table: 'pf',
    data: {
      pf_ativo: 1,
      pf_id: `!IN!(SELECT pf_id FROM pf WHERE ${sqlDigitsOnlyColumn('pf_cpf')} = '${digits}')`,
    },
  })}`);
  const rows = Array.isArray(response?.data) ? response.data : [];
  return rows.find((row) => normalizeDocumentoDigits(row.pf_cpf) === digits) || rows[0] || null;
};

export const fetchPjByCnpj = async (cnpj) => {
  const digits = normalizeDocumentoDigits(cnpj);
  if (!digits) return null;

  const response = await oikosApi.get(`/?${qs.stringify({
    action: 'get',
    table: 'pj',
    data: {
      pj_ativo: 1,
      pj_id: `!IN!(SELECT pj_id FROM pj WHERE ${sqlDigitsOnlyColumn('pj_cnpj')} = '${digits}')`,
    },
  })}`);
  const rows = Array.isArray(response?.data) ? response.data : [];
  return rows.find((row) => normalizeDocumentoDigits(row.pj_cnpj) === digits) || rows[0] || null;
};

/** Retorna 'pf' | 'pj' | null conforme CPF/CNPJ válido. */
export const resolveDocumentoTipo = (documento) => {
  const digits = normalizeDocumentoDigits(documento);
  if (!digits) return null;
  if (validarCPF(digits)) return 'pf';
  if (validarCNPJ(digits)) return 'pj';
  return null;
};

/**
 * Busca favorecido por CPF/CNPJ.
 * @returns {{ tipo: 'pf'|'pj', digits: string, existing: object|null }}
 */
export const lookupFavorecidoByDocumento = async (documento) => {
  const digits = normalizeDocumentoDigits(documento);
  const tipo = resolveDocumentoTipo(digits);
  if (!tipo) {
    throw new Error('Informe um CPF ou CNPJ válido.');
  }

  if (tipo === 'pf') {
    return { tipo, digits, existing: await fetchPfByCpf(digits) };
  }

  return { tipo, digits, existing: await fetchPjByCnpj(digits) };
};

export const upsertFornecedorFromCompraCotacaoInfo = async (info, userId) => {
  const identificacao = resolveIdentificadorFromInfo(info);
  if (!identificacao) {
    throw new Error('CPF ou CNPJ inválido ou ausente nas informações do fornecedor.');
  }

  const payload = buildFornecedorPayloadFromInfo(info, {
    isPf: identificacao.isPf,
    userId,
  });
  if (!payload) {
    throw new Error('Não foi possível montar os dados cadastrais do fornecedor.');
  }

  if (identificacao.isPf) {
    const existingPf = await fetchPfByCpf(identificacao.digits);
    if (existingPf?.pf_id) {
      await oikosApi.post('/', {
        action: 'update',
        table: 'pf',
        data: {
          pf_id: existingPf.pf_id,
          ...payload,
        },
      });
      return { contrForn_parteTab: 'pf', contrForn_parteId: existingPf.pf_id };
    }

    if (!getNomeOuRazaoSocialFromInfo(info)) {
      throw new Error('Informe o nome ou razão social do fornecedor para concluir o cadastro.');
    }

    const response = await oikosApi.post('/', {
      action: 'insert',
      table: 'pf',
      data: {
        ...payload,
        pf_usuarioCria: userId,
      },
    });
    const created = response?.data?.[0];
    if (!created?.pf_id) {
      throw new Error('Não foi possível cadastrar a pessoa física do fornecedor.');
    }
    return { contrForn_parteTab: 'pf', contrForn_parteId: created.pf_id };
  }

  const existingPj = await fetchPjByCnpj(identificacao.digits);
  if (existingPj?.pj_id) {
    await oikosApi.post('/', {
      action: 'update',
      table: 'pj',
      data: {
        pj_id: existingPj.pj_id,
        ...payload,
      },
    });
    return { contrForn_parteTab: 'pj', contrForn_parteId: existingPj.pj_id };
  }

  if (!getNomeOuRazaoSocialFromInfo(info)) {
    throw new Error('Informe o nome ou razão social do fornecedor para concluir o cadastro.');
  }

  const response = await oikosApi.post('/', {
    action: 'insert',
    table: 'pj',
    data: {
      ...payload,
      pj_usuarioCria: userId,
    },
  });
  const created = response?.data?.[0];
  if (!created?.pj_id) {
    throw new Error('Não foi possível cadastrar a pessoa jurídica do fornecedor.');
  }
  return { contrForn_parteTab: 'pj', contrForn_parteId: created.pj_id };
};

export const buildSupplierValidationContrFornUpdateData = ({
  contrFornId,
  info,
  contrFornData = {},
  parte = {},
  userId,
  contrFornStatusId = 7,
  includeMinutaMod = true,
}) => {
  const objetoResumido = info?.informacoes_contrato?.objeto_resumido
    ? String(info.informacoes_contrato.objeto_resumido).trim()
    : undefined;

  return {
    contrForn_id: Number(contrFornId),
    contrForn_parteTab: parte.contrForn_parteTab,
    contrForn_parteId: parte.contrForn_parteId,
    contrForn_detalhamento: contrFornData.contrForn_detalhamento || undefined,
    contrForn_obj: objetoResumido,
    contrForn_desc: objetoResumido || contrFornData.contrForn_desc || undefined,
    contrForn_vlrGlobal: getPropostaValorFromInfo(info) ?? contrFornData.contrForn_vlrGlobal ?? undefined,
    ...(includeMinutaMod
      ? { minutaMod_id: Number(contrFornData.minutaMod_id) || undefined }
      : {}),
    contrFornStatus_id: Number(contrFornStatusId) || 7,
    pf_id: userId,
  };
};

export const resolveParteFromIdentificador = async (identificador) => {
  const digits = normalizeDocumentoDigits(identificador);
  if (!digits) return null;

  if (validarCPF(digits)) {
    const pf = await fetchPfByCpf(digits);
    if (pf?.pf_id) {
      return { contrForn_parteTab: 'pf', contrForn_parteId: pf.pf_id };
    }
  }

  if (validarCNPJ(digits)) {
    const pj = await fetchPjByCnpj(digits);
    if (pj?.pj_id) {
      return { contrForn_parteTab: 'pj', contrForn_parteId: pj.pj_id };
    }
  }

  return null;
};

export const buildContrFornPrefillFromInfo = async (info, compraCotacaoRow = {}) => {
  const prefill = {};
  const compraCotacaoUpdates = {};

  const objetoResumido = info?.informacoes_contrato?.objeto_resumido;
  if (objetoResumido) {
    prefill.contrForn_desc = String(objetoResumido).trim();
  }

  const valorFromInfo = getPropostaValorFromInfo(info);
  const valorFromRow = Number(compraCotacaoRow?.compraCotacao_vlr) || 0;
  prefill.contrForn_vlrGlobal = valorFromInfo ?? (valorFromRow || 0);

  const email = info?.dados_pessoais?.email;
  if (email) {
    compraCotacaoUpdates.compraCotacao_emailInteressado = String(email).trim();
  }

  const identificador = info?.identificacao?.cpf_ou_cnpj;
  try {
    const parte = await resolveParteFromIdentificador(identificador);
    if (parte) {
      prefill.contrForn_parteTab = parte.contrForn_parteTab;
      prefill.contrForn_parteId = parte.contrForn_parteId;
    }
  } catch (error) {
    console.error('Erro ao resolver contratado a partir do identificador:', error);
  }

  return { prefill, compraCotacaoUpdates };
};

export const fetchCompraCotacaoWithInfo = async (compraCotacaoId) => {
  const normalizedId = Number(compraCotacaoId) || 0;
  if (!normalizedId) return null;

  const response = await oikosApi.get(`/?${qs.stringify({
    action: 'get',
    table: 'compraCotacao',
    data: { compraCotacao_id: normalizedId },
  })}`);

  return response?.data?.[0] || null;
};

export const getFavorecidoPagamento = (info) => {
  const fav = info?.favorecido_pagamento;
  if (!fav || typeof fav !== 'object') return null;

  return {
    razao_social: String(fav.razao_social || '').trim(),
    telefone: String(fav.telefone || '').trim(),
    email: String(fav.email || '').trim(),
    cnpj: String(fav.cnpj || '').trim(),
  };
};

export const hasFavorecidoPagamentoCompleto = (favorecido) => {
  const fav = getFavorecidoPagamento({ favorecido_pagamento: favorecido });
  if (!fav) return false;
  return Boolean(fav.razao_social && fav.telefone && fav.email && fav.cnpj);
};

/** Merge favorecido_pagamento into existing compraCotacao_info without replacing other keys. */
export const mergeFavorecidoPagamento = (rawInfo, favorecido = {}) => {
  const parsed = parseCompraCotacaoInfo(rawInfo);
  const base = parsed && typeof parsed === 'object' && !Array.isArray(parsed)
    ? { ...parsed }
    : {};

  base.favorecido_pagamento = {
    razao_social: String(favorecido.razao_social || '').trim(),
    telefone: String(favorecido.telefone || '').trim(),
    email: String(favorecido.email || '').trim(),
    cnpj: String(favorecido.cnpj || '').replace(/\D/g, ''),
  };

  return JSON.stringify(base);
};

export { PROPOSTA_TP_DOCUMENTO_ID };
